import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button, Divider, Flex, Form, Input, Select, Spin, Tooltip, message } from 'antd';
import TextArea from 'antd/es/input/TextArea';

import ScenarioEnhanceModal from './ScenarioEnhanceModal';

import ScenarioEditionFormContext from '../../contexts/forms/scenarios/ScenarioEditionFormContext';

import ScenarioBusinessPriority, { renderScenarioBusinessPriorityOption } from '../../../domain/enums/scenario/ScenarioBusinessPriority';
import ContactQualificationAiQualification, { renderContactQualificationAiQualificationOption } from '../../../domain/enums/contact/contact_qualification_ai/ContactQualificationAiQualification';

import ScenarioCriteriaEditor from '../@shared/scenarios/ScenarioCriteriaEditor';
import ScenarioSamplesEditor from '../@shared/scenarios/ScenarioSamplesEditor';

import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { UserRole, ContextStatus } from '@leedpulse-monorepo/enums';
import { FormCard, FormContainer } from '@leedpulse-monorepo/components';

import { DOMAINS_PAGE_PATH } from '../../pages/domains/DomainsPage';
import Scenario from '../../../domain/models/Scenario';
import { CheckCircleFilled, CloseCircleFilled, FileTextOutlined, InfoCircleOutlined, LeftOutlined, ManOutlined, MessageOutlined, QuestionCircleOutlined, RightOutlined, RobotOutlined, SnippetsOutlined } from '@ant-design/icons';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import moment from 'moment';
import ScenarioElementType from '../../../domain/enums/scenario/ScenarioElementType';
import { useNavigate } from 'react-router-dom';
import { FirestoreCollections } from '@leedpulse-monorepo/firebase-metadata';

const { Option } = Select;
interface ScenarioDetailsProps {
  scenario: Scenario;
  onDemoChangeVisibility: (isVisible: boolean) => void;
}

const MAX_LENGTH_TEXT_AREA = 6000;

const ScenarioDetails: React.FC<ScenarioDetailsProps> = ({ scenario, onDemoChangeVisibility }) => {
  const navigate = useNavigate();
  const scenarioEditionFormContext = useContext(ScenarioEditionFormContext);
  const { wipScenario } = scenarioEditionFormContext;
  const windowSize = useWindowSize();
  const authContext = useContext(AuthenticationContext);

  // Enhancement modal state
  const [enhanceModalOpen, setEnhanceModalOpen] = useState(false);
  const [elementTypeToEnhance, setElementTypeToEnhance] = useState<ScenarioElementType | null>(null);

  const [isDemoVisible, setIsDemoVisible] = useState(true);
  const [activeTabKey, setActiveTabKey] = useState('1');

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin].includes(
      authContext.authentication?.role
    );

  const isBusy =
    scenarioEditionFormContext.updateStatus === ContextStatus.LOADING ||
    scenarioEditionFormContext.deleteStatus === ContextStatus.LOADING;

  useEffect(() => {
    if (!wipScenario) {
      scenarioEditionFormContext.initWithScenario(scenario);
    }
  }, [scenario, wipScenario, scenarioEditionFormContext]);

  useEffect(() => {
    if (scenarioEditionFormContext.updateStatus === ContextStatus.SUCCESS) {
      message.success('Scénario mis à jour avec succès');
    } else if (
      scenarioEditionFormContext.updateStatus === ContextStatus.ERROR
    ) {
      message.error('Erreur lors de la mise à jour du scénario');
    }
  }, [scenarioEditionFormContext.updateStatus]);

  const handleOpenEnhanceModal = (type: ScenarioElementType) => {
    setElementTypeToEnhance(type);
    setEnhanceModalOpen(true);
  };

  useEffect(() => {
    if (scenarioEditionFormContext.deleteStatus === ContextStatus.SUCCESS) {
      message.success('Scénario supprimé avec succès');
      navigate(`${DOMAINS_PAGE_PATH}/${scenario.idDomain}`);
    } else if (
      scenarioEditionFormContext.deleteStatus === ContextStatus.ERROR
    ) {
      message.error(scenarioEditionFormContext.errorMessage);
    }
  }, [scenarioEditionFormContext.deleteStatus, navigate, scenario.idDomain]);

  if (wipScenario === null) {
    return <Spin />;
  }

  const tabList = [
    {
      key: '1',
      tab: <Flex align="center"><InfoCircleOutlined style={{ marginRight: 6 }} />Informations</Flex>
    },
    {
      key: '2',
      tab: <Flex align="center"><QuestionCircleOutlined style={{ marginRight: 6 }} /> Qualification</Flex>
    },
    {
      key: '3',
      tab: <Flex align="center"><SnippetsOutlined style={{ marginRight: 6 }} /> Modèles</Flex>
    }
  ];

  const handleDemoVisibilityClickBtn = () => {
    setIsDemoVisible(!isDemoVisible);
    onDemoChangeVisibility(!isDemoVisible);
  }

  const handleTabChange = (key: string) => {
    setActiveTabKey(key);
  }

  const renderHeaderActions = () => {
    if (windowSize.width < 1512) {
      return [
        <Tooltip key="toggle-demo" title={isDemoVisible ? 'Masquer la demo' : 'Afficher la demo'}>
          <Button
            type="default"
            icon={isDemoVisible ? <RightOutlined /> : <LeftOutlined />}
            style={{ marginRight: '1rem' }}
            onClick={handleDemoVisibilityClickBtn} >
            <span style={{ display: 'none' }} />
          </Button>
        </Tooltip>
      ]
    }

    return [
      <Button
        key="toggle-demo"
        type="default"
        icon={<MessageOutlined />}
        style={{ marginRight: '1rem' }}
        onClick={handleDemoVisibilityClickBtn}
      >
        {isDemoVisible ? <span className="extra-label">Masquer la demo</span> : <span className="extra-label">Afficher la demo</span>}
      </Button>
    ]
  }

  const contentList: Record<string, React.ReactNode> = {
    1: <Form layout='vertical'>
      <FormContainer twoColumnsMax>
        <Form.Item label="Nom">
          <Input
            maxLength={100}
            showCount
            placeholder="Acheteur - description"
            value={wipScenario.name}
            onChange={(e) =>
              scenarioEditionFormContext.onNameChange(e.target.value)
            }
            disabled={isBusy || isReadOnly}
          />
        </Form.Item>

        <Form.Item label="Nom court (affiché au client)">
          <Input
            maxLength={25}
            showCount
            placeholder="Acheteur"
            value={wipScenario.nameShort}
            onChange={(e) =>
              scenarioEditionFormContext.onNameShortChange(e.target.value)
            }
            disabled={isBusy || isReadOnly}
          />
        </Form.Item>

        <Form.Item label="Priorité">
          <Select
            value={wipScenario.businessPriority}
            onChange={(value) =>
              scenarioEditionFormContext.onBusinessPriorityChange(value)
            }
            disabled={isBusy || isReadOnly}
          >
            {Object.keys(ScenarioBusinessPriority).map((key) => {
              const priority =
                ScenarioBusinessPriority[
                key as keyof typeof ScenarioBusinessPriority
                ];
              return (
                <Option key={key} value={priority}>
                  {renderScenarioBusinessPriorityOption(priority)}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
      </FormContainer>

      <FormContainer twoColumnsMax>
        <Form.Item
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tooltip title="Améliorer avec l'IA">
                <Button
                  type="text"
                  variant='outlined'
                  size='small'
                  icon={<RobotOutlined />}
                  onClick={() => handleOpenEnhanceModal(ScenarioElementType.description)}
                />
              </Tooltip>
              <span style={{ marginRight: 'auto' }}>
                Description
              </span>
            </div>
          }
        >
          <TextArea
            maxLength={MAX_LENGTH_TEXT_AREA}
            rows={7}
            showCount
            placeholder="Ce scénario correspond aux prospects souhaitant acheter un bien immobilier"
            value={wipScenario.description}
            onChange={(e) =>
              scenarioEditionFormContext.onDescriptionChange(e.target.value)
            }
            disabled={isBusy || isReadOnly}
          />
        </Form.Item>
        <Form.Item
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tooltip title="Améliorer avec l'IA">
                <Button
                  type="text"
                  variant='outlined'
                  size='small'
                  icon={<RobotOutlined />}
                  onClick={() => handleOpenEnhanceModal(ScenarioElementType.target)}
                />
              </Tooltip>
              <span style={{ marginRight: 'auto' }}>
                But à atteindre
              </span>
            </div>
          }
        >
          <TextArea
            maxLength={MAX_LENGTH_TEXT_AREA}
            rows={7}
            showCount
            placeholder="Qualifier le prospect et proposer un RDV téléphonique"
            value={wipScenario.target}
            onChange={(e) =>
              scenarioEditionFormContext.onTargetChange(e.target.value)
            }
            disabled={isBusy || isReadOnly}
          />
        </Form.Item>
      </FormContainer>

      <FormContainer twoColumnsMax>
        <Form.Item
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tooltip title="Améliorer avec l'IA">
                <Button
                  type="text"
                  variant='outlined'
                  size='small'
                  icon={<RobotOutlined />}
                  onClick={() => handleOpenEnhanceModal(ScenarioElementType.constraints)}
                />
              </Tooltip>
              <span style={{ marginRight: 'auto' }}>
                Contraintes
              </span>
            </div>
          }
        >
          <TextArea
            maxLength={MAX_LENGTH_TEXT_AREA}
            rows={7}
            showCount
            placeholder="Le prospect doit être qualifié avant de lui prosposer un appel téléphonique"
            value={wipScenario.constraints}
            onChange={(e) =>
              scenarioEditionFormContext.onConstraintsChange(e.target.value)
            }
            disabled={isBusy || isReadOnly}
          />
        </Form.Item>

        <Form.Item
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tooltip title="Améliorer avec l'IA">
                <Button
                  type="text"
                  variant='outlined'
                  size='small'
                  icon={<RobotOutlined />}
                  onClick={() => handleOpenEnhanceModal(ScenarioElementType.behavior)}
                />
              </Tooltip>
              <span style={{ marginRight: 'auto' }}>
                Comportement
              </span>
            </div>
          }
        >
          <TextArea
            maxLength={MAX_LENGTH_TEXT_AREA}
            rows={7}
            showCount
            placeholder="Pose des questions directes sur le projet immobilier"
            value={wipScenario.behavior}
            onChange={(e) =>
              scenarioEditionFormContext.onBehaviorChange(e.target.value)
            }
            disabled={isBusy || isReadOnly}
          />
        </Form.Item>
      </FormContainer>

      <FormContainer twoColumnsMax>
        <Form.Item label="Règles de notification">
          <Select
            mode="multiple"
            placeholder="Sélectionnez la ou les règles de notification"
            value={wipScenario.escalationRules || []}
            onChange={(vals) =>
              scenarioEditionFormContext.onEscalationRulesChange(
                vals as ContactQualificationAiQualification[]
              )
            }
            disabled={isBusy || isReadOnly}
          >
            {Object.keys(ContactQualificationAiQualification).map((key) => {
              const rule =
                ContactQualificationAiQualification[
                key as keyof typeof ContactQualificationAiQualification
                ];
              return (
                <Option key={key} value={rule}>
                  {renderContactQualificationAiQualificationOption(rule)}
                </Option>
              );
            })}
          </Select>
        </Form.Item>

        <Form.Item
          label="Qualif requise avant notification"
          tooltip={"Cette option impose la qualification du prospect avant l'envoi d'une notification, limitant ainsi le nombre de notifications envoyées au conseiller attitré du contact."}>
          <Select
            value={wipScenario.escalationNeedsQualification ? 'Oui' : 'Non'}
            onChange={(value) =>
              scenarioEditionFormContext.onEscalationNeedsQualificationChange(value === 'Oui')
            }
          >
            <Option value="Oui"><CheckCircleFilled style={{ color: 'green', marginRight: '8px' }} />Oui</Option>
            <Option value="Non"><CloseCircleFilled style={{ color: 'red', marginRight: '8px' }} /> Non</Option>
          </Select>
        </Form.Item>
      </FormContainer>

      <FormContainer twoColumnsMax>
        <Form.Item
          label="Fidélité du 1er message"
          tooltip={"Détermine si le premier message doit être strictement fidèle au modèle défini."}>
          <Select
            value={wipScenario.fidelityModelFirstMessage ? 'Oui' : 'Non'}
            onChange={(value) =>
              scenarioEditionFormContext.onFidelityModelFirstMessageChange(value === 'Oui')
            }
            disabled={isBusy || isReadOnly}
          >
            <Option value="Oui"><CheckCircleFilled style={{ color: 'green', marginRight: '8px' }} />Oui</Option>
            <Option value="Non"><CloseCircleFilled style={{ color: 'red', marginRight: '8px' }} /> Non</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Lien de réservation Calendly"
          tooltip={"Lien vers lequel le prospect sera redirigé pour réserver un rendez-vous."}>
          <Input
            value={wipScenario.link_calendar_booking}
            placeholder="https://calendly.com/..."
            onChange={(e) =>
              scenarioEditionFormContext.onLinkCalendarBookingChange(e.target.value)
            }
            disabled={isBusy || isReadOnly}
          />
        </Form.Item>
      </FormContainer>
      <Divider />
      <p>
        Créé le <b>{moment(wipScenario.created).format('LLL')}</b><br />
        Dernière modification <b>{moment(wipScenario.updated).fromNow()}</b>
      </p>
    </Form >,
    2: <Form layout='vertical'>
      <Form.Item label={<Flex align="center"><FileTextOutlined style={{ marginRight: 6 }} /> Instructions de qualification</Flex>}>
        <TextArea
          maxLength={MAX_LENGTH_TEXT_AREA}
          rows={7}
          showCount
          placeholder="Ici, donne les instructions relatives à la qualification d'un prospect. (e.g : 'Un prospect est considéré comme intéressé, si il a un projet d'achat dans les 6 mois et un budget supérieur à 200 000€. Sinon, il est considéré comme non intéressé.')"
          value={wipScenario.qualificationInstructions || ''}
          onChange={(e) =>
            scenarioEditionFormContext.onQualificationInstructionsChange(e.target.value)
          }
          disabled={isBusy || isReadOnly}
        />
      </Form.Item>
      <Alert
        description="Chaque élément doit être formulé sous forme de question (ex: 'Quel est votre budget ?')"
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
      />
      <Form.Item>
        <ScenarioCriteriaEditor
          criteria={wipScenario.criteria}
          isBusy={isBusy}
          onChange={(newCriteria) =>
            scenarioEditionFormContext.onCriteriaChange(newCriteria)
          }
          disabled={isReadOnly}
        />
      </Form.Item>
    </Form>,
    3: <Form layout='vertical'>
      <Alert
        description={
          <Flex vertical>
            <p>
              Utilisez le symbole # pour insérer une variable (ex: 'Bonjour #<ManOutlined />-prospect-firstname, comment puis-je vous aider ?')
            </p>
            <p>
              L'ajout d'un contexte permet d'aiguiller l'agent dans l'utilisation des modèles. (ex: 'Le client demande un appel téléphonique')
            </p>
          </Flex>
        }
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
      />
      {wipScenario.samples && wipScenario.samples.length > 0 && wipScenario.samples.filter(sample => sample.type === 'first_contact').length > 1 && (
        <Alert
          message="Attention"
          description="Il y a plusieurs modèles avec le type 'first_contact'. Seul le premier dans cette liste sera pris en compte."
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}
      <Form.Item>
        <ScenarioSamplesEditor
          samples={wipScenario.samples}
          isBusy={isBusy}
          onChange={(newSamples) =>
            scenarioEditionFormContext.onSamplesChange(newSamples)
          }
          disabled={isReadOnly}
        />
      </Form.Item>
    </Form>
  };

  /**
   * Open the enhance modal for a specific element type
   */

  /**
   * Handle applying enhanced value from the modal
   */
  const handleApplyEnhancedValue = (type: ScenarioElementType, value: string) => {
    switch (type) {
      case ScenarioElementType.description:
        scenarioEditionFormContext.onDescriptionChange(value);
        break;
      case ScenarioElementType.target:
        scenarioEditionFormContext.onTargetChange(value);
        break;
      case ScenarioElementType.constraints:
        scenarioEditionFormContext.onConstraintsChange(value);
        break;
      case ScenarioElementType.behavior:
        scenarioEditionFormContext.onBehaviorChange(value);
        break;
    }
  };

  return (
    <>
      <FormCard
        title={wipScenario.id || 'Détails du scénario'}
        collectionName={FirestoreCollections.scenarios}
        onUpdated={
          isReadOnly
            ? undefined
            : () => scenarioEditionFormContext.submitUpdate()
        }
        onDeleted={
          isReadOnly
            ? undefined
            : () => scenarioEditionFormContext.submitDelete()
        }
        isUpdateLoading={isBusy}
        isDeleteLoading={isBusy}
        headerActions={renderHeaderActions()}
        tabList={tabList}
        onTabChange={handleTabChange}
        activeTabKey={activeTabKey}
      >
        {contentList[activeTabKey]}
      </FormCard>

      <ScenarioEnhanceModal
        open={enhanceModalOpen}
        elementType={elementTypeToEnhance}
        currentValue={elementTypeToEnhance ? wipScenario[elementTypeToEnhance] : ''}
        scenarioId={wipScenario.id}
        onClose={() => setEnhanceModalOpen(false)}
        onApply={handleApplyEnhancedValue}
      />
    </>
  );
};

export default ScenarioDetails;
